import { CUSTOM_EVENT_HIGHLIGHT_CLICK } from '../../constants'
import { IHTMLCompareChangeItem } from '../types'

/**
 * Highlight style data attribute for version comparison
 */
const HIGHLIGHT_STYLE_DATA_ATTR = 'data-highlight-style'
/**
 * Compare highlight class name for version comparison
 */
export const COMPARE_HIGHLIGHT_CLASS_NAME = 'compare-highlight'

/**
 * Ensure highlight styles are applied to the shadow root
 * @param params - The parameters for the function
 * @param params.shadowRoot - The shadow root to apply the styles to
 * @param params.isSource - Whether the source version is being compared
 * @param params.isTarget - Whether the target version is being compared
 */
const ensureHighlightStyles = (params: {
  shadowRoot: ShadowRoot
  isSource?: boolean
  isTarget?: boolean
}) => {
  const { shadowRoot, isSource, isTarget } = params

  const existingStyle = shadowRoot.querySelector(
    `style[${HIGHLIGHT_STYLE_DATA_ATTR}]`
  )
  if (existingStyle) return

  const style = document.createElement('style')
  style.setAttribute(HIGHLIGHT_STYLE_DATA_ATTR, 'true')
  if (isSource) {
    style.textContent = `
      .${COMPARE_HIGHLIGHT_CLASS_NAME} {
        background: #FEE4E2;
        padding: 0 1px;
        cursor: pointer;
      }
    `
  } else if (isTarget) {
    style.textContent = `
      .${COMPARE_HIGHLIGHT_CLASS_NAME} {
        background: #D1E9FF;
        padding: 0 1px;
        cursor: pointer;
      }
    `
  }

  shadowRoot.appendChild(style)
}

/**
 * Clear all highlights from the shadow root
 * @param shadowRoot - The shadow root to clear the highlights from
 */
const clearAllHighlights = (shadowRoot: ShadowRoot) => {
  const highlights = shadowRoot.querySelectorAll(
    `.${COMPARE_HIGHLIGHT_CLASS_NAME}`
  )
  highlights.forEach((highlight) => {
    const parent = highlight.parentNode
    if (!parent) return

    const text = shadowRoot.ownerDocument.createTextNode(
      highlight.textContent ?? ''
    )
    highlight.replaceWith(text)
    parent.normalize()
  })
}

/**
 * Highlight text in element
 * @param params - The parameters for the function
 * @param params.element - The element to highlight the text in
 * @param params.text - The text to highlight
 * @param params.shadowRoot - The shadow root to apply the highlights to
 * @param params.uuid - The UUID of the change
 * @param params.prefix - The prefix to highlight
 * @param params.suffix - The suffix to highlight
 * */
/**
 * Normalize whitespace in text by collapsing multiple spaces and trimming edges.
 * Used to handle different whitespace representations in HTML and improve text matching.
 * @param value - The string to normalize
 * @returns Normalized string with collapsed whitespace
 */
const normalizeText = (value: string) => value.replace(/\s+/g, ' ').trim() // NOSONAR

/**
 * Collect all eligible text nodes from an element for highlighting.
 * Uses TreeWalker to traverse the DOM and filters out:
 * - Text nodes in script/style/pre/code/textarea elements (non-renderable content)
 * - Text nodes already wrapped in highlights (to avoid re-highlighting)
 * @param element - The element to collect text nodes from
 * @param documentRef - The document reference for creating tree walker
 * @returns Array of Text nodes that are candidates for highlighting
 */
const getTextNodes = (element: Element, documentRef: Document) => {
  const walker = documentRef.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const p = node.parentElement
      if (!p) return NodeFilter.FILTER_REJECT
      if (p.closest('script,style,pre,code,textarea'))
        return NodeFilter.FILTER_REJECT
      return NodeFilter.FILTER_ACCEPT
    },
  })
  const textNodes: Text[] = []
  let current = walker.nextNode()
  while (current) {
    const parent = (current as Text).parentElement
    if (parent?.classList.contains(COMPARE_HIGHLIGHT_CLASS_NAME)) {
      current = walker.nextNode()
      continue
    }
    textNodes.push(current as Text)
    current = walker.nextNode()
  }

  return textNodes
}

/**
 * Create a highlight wrapper span element that can notify the sidebar when clicked.
 * The span has a UUID data attribute for tracking and a click handler that dispatches
 * a custom event to focus the change in the sidebar.
 * @param documentRef - Document reference for creating elements
 * @param text - The text content to wrap
 * @param uuid - Optional UUID to track this highlight and enable sidebar navigation
 * @returns A span element with highlight styling and event handlers
 */
const createHighlightMark = (
  documentRef: Document,
  text: string,
  uuid?: string
) => {
  const mark = documentRef.createElement('span')
  mark.className = COMPARE_HIGHLIGHT_CLASS_NAME
  mark.textContent = text
  if (uuid) {
    mark.dataset.uuid = uuid

    // Add click handler to focus the change in sidebar
    mark.addEventListener('click', (e) => {
      e.stopPropagation()

      // Dispatch custom event to set focused UUID
      const event = new CustomEvent(CUSTOM_EVENT_HIGHLIGHT_CLICK, {
        detail: { uuid },
      })
      globalThis.dispatchEvent(event)
    })
  }

  return mark
}

/**
 * Check if a text node contains multiple matches of a substring.
 * If multiple matches exist, context (prefix/suffix) should be used to disambiguate which one to highlight.
 * @param nodeValue - The full text node value
 * @param text - The text to search for
 * @returns True if text appears multiple times in nodeValue, false otherwise
 */
const shouldUseContextMatch = (nodeValue: string, text: string) => {
  if (!text) return false
  return nodeValue.split(text).length - 1 > 1
}

/**
 * Apply a context-aware highlight by using prefix and suffix to identify the exact match.
 * This is used when a text appears multiple times in a node and we need to highlight only one occurrence.
 * Builds a document fragment with text nodes and highlight spans positioned correctly.
 * @param params - Configuration object
 * @param params.documentRef - Document reference for creating elements
 * @param params.fragment - Fragment to append nodes to
 * @param params.nodeValue - The full text node value
 * @param params.text - The text to highlight
 * @param params.prefix - Optional prefix text for context matching
 * @param params.suffix - Optional suffix text for context matching
 * @param params.uuid - Optional UUID for tracking the highlight
 * @returns True if highlight was successfully applied, false if context not found
 */
const applyContextHighlight = (params: {
  documentRef: Document
  fragment: DocumentFragment
  nodeValue: string
  text: string
  prefix?: string
  suffix?: string
  uuid?: string
}) => {
  const { documentRef, fragment, nodeValue, text, prefix, suffix, uuid } =
    params
  const normalizedPrefix = normalizeText(prefix ?? '')
  const normalizedSuffix = normalizeText(suffix ?? '')
  const normalizedText = normalizeText(text)
  const fullText = `${normalizedPrefix}${normalizedText}${normalizedSuffix}`
  const fullIndex = nodeValue.indexOf(fullText)
  if (fullIndex === -1) return false

  const highlightStart = fullIndex + normalizedPrefix.length
  const highlightEnd = highlightStart + normalizedText.length

  if (highlightStart > 0) {
    fragment.appendChild(
      documentRef.createTextNode(nodeValue.slice(0, highlightStart))
    )
  }

  fragment.appendChild(createHighlightMark(documentRef, text, uuid))

  if (highlightEnd < nodeValue.length) {
    fragment.appendChild(
      documentRef.createTextNode(nodeValue.slice(highlightEnd))
    )
  }

  return true
}

/**
 * Apply highlights for every occurrence of a text substring in a node.
 * This is used when there are multiple matches and no context is needed, or context matching failed.
 * Builds a document fragment with all matches wrapped in highlight spans.
 * @param params - Configuration object
 * @param params.documentRef - Document reference for creating elements
 * @param params.fragment - Fragment to append nodes to
 * @param params.nodeValue - The full text node value
 * @param params.text - The text to highlight
 * @param params.uuid - Optional UUID for tracking the highlight
 */
const applyAllMatchesHighlight = (params: {
  documentRef: Document
  fragment: DocumentFragment
  nodeValue: string
  text: string
  uuid?: string
}) => {
  const { documentRef, fragment, nodeValue, text, uuid } = params
  let remaining = nodeValue
  let matchIndex = remaining.indexOf(text)

  while (matchIndex !== -1) {
    if (matchIndex > 0) {
      fragment.appendChild(
        documentRef.createTextNode(remaining.slice(0, matchIndex))
      )
    }

    fragment.appendChild(createHighlightMark(documentRef, text, uuid))

    remaining = remaining.slice(matchIndex + text.length)
    matchIndex = remaining.indexOf(text)
  }

  if (remaining) {
    fragment.appendChild(documentRef.createTextNode(remaining))
  }
}

/**
 * Apply text highlighting within a target element by wrapping matching text in span elements.
 * Handles both context-aware and all-matches highlighting strategies based on the number of matches.
 * Replaces text nodes with document fragments containing text + highlight spans.
 * @param params - Configuration object
 * @param params.element - The element to highlight text within
 * @param params.text - The text to highlight
 * @param params.shadowRoot - The shadow root for document reference
 * @param params.uuid - Optional UUID for tracking
 * @param params.prefix - Optional context prefix for disambiguation
 * @param params.suffix - Optional context suffix for disambiguation
 */
const highlightTextInElement = (params: {
  element: Element
  text: string
  shadowRoot: ShadowRoot
  uuid?: string
  prefix?: string
  suffix?: string
}) => {
  const { element, text, shadowRoot, uuid, prefix, suffix } = params
  if (!text) return

  const documentRef = shadowRoot.ownerDocument
  const textNodes = getTextNodes(element, documentRef)

  textNodes.forEach((textNode) => {
    const nodeValue = normalizeText(textNode.nodeValue ?? '')
    if (!nodeValue.includes(text)) return

    const fragment = documentRef.createDocumentFragment()
    const normalizedText = normalizeText(text)

    const canUseContext =
      shouldUseContextMatch(nodeValue, normalizedText) && (prefix || suffix)

    const appliedContext =
      canUseContext &&
      applyContextHighlight({
        documentRef,
        fragment,
        nodeValue,
        text,
        prefix,
        suffix,
        uuid,
      })

    if (!appliedContext) {
      applyAllMatchesHighlight({
        documentRef,
        fragment,
        nodeValue,
        text,
        uuid,
      })
    }

    textNode.replaceWith(fragment)
  })
}

/**
 * Highlight change by path
 * @param params - The parameters for the function
 * @param params.shadowRoot - The shadow root to apply the highlights to
 * @param params.diff - The diff operation to apply the highlights to
 * @param params.content - The content to highlight
 * @param params.prefix - The prefix to highlight
 * @param params.suffix - The suffix to highlight
 */
const highlightChangeByPath = (params: {
  shadowRoot: ShadowRoot
  diff: IHTMLCompareChangeItem
  content: string
  prefix?: string
  suffix?: string
}) => {
  const { shadowRoot, diff, content, prefix, suffix } = params
  if (!diff.path) return

  const element = shadowRoot.querySelector(diff.path)
  if (!element) return

  const targetText = content.trim()
  if (!targetText) return

  highlightTextInElement({
    element,
    text: targetText,
    shadowRoot,
    uuid: diff.uuid,
    prefix,
    suffix,
  })
}

/**
 * Apply highlights for version comparison
 * @param params - The parameters for the function
 * @param params.shadowRoot - The shadow root to apply the highlights to
 * @param params.diffOps - The diff operations to apply the highlights to
 * @param params.isSource - Whether the source version is being compared
 * @param params.isTarget - Whether the target version is being compared
 */
export const applySourceDiffHighlights = (params: {
  shadowRoot: ShadowRoot
  diffOps: IHTMLCompareChangeItem[]
  isSource?: boolean
  isTarget?: boolean
}) => {
  const { shadowRoot, diffOps, isSource, isTarget } = params
  ensureHighlightStyles({ shadowRoot, isSource, isTarget })
  clearAllHighlights(shadowRoot)

  diffOps.forEach((diff) => {
    let content = diff.oldValue ?? ''
    let prefix = diff.oldPrefix ?? ''
    let suffix = diff.oldSuffix ?? ''

    if (isTarget) {
      content = diff.newValue ?? ''
      prefix = diff.newPrefix ?? ''
      suffix = diff.newSuffix ?? ''
    }

    if (typeof content !== 'string' || !content) return
    highlightChangeByPath({ shadowRoot, diff, content, prefix, suffix })
  })
}
